import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchProductVariant, ProductVariant, updateProductVariantActive } from '../../api/productVariants';
import PageHeaderWithBack from '../../components/PageHeaderWithBack';
import { fetchInventories, PaginatedInventoriesResponse } from '../../api/inventories';
import { fetchCurrentUser, type MeResponse } from '../../api/auth';
import { fetchProductRelatedVariants, type ProductRelatedVariant } from '../../api/productRelatedVariants';

interface VariantsDetailPanelProps {
  id: string;
  onBack: () => void;
  servicesOnly?: boolean;
}

const VariantsDetailPanel: FC<VariantsDetailPanelProps> = ({ id, onBack, servicesOnly = false }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<ProductVariant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inv, setInv] = useState<PaginatedInventoriesResponse | null>(null);
  const [invLoading, setInvLoading] = useState(false);
  const [invError, setInvError] = useState<string | null>(null);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [savingStatus, setSavingStatus] = useState(false);
  const [related, setRelated] = useState<ProductRelatedVariant[] | null>(null);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [relatedError, setRelatedError] = useState<string | null>(null);
  const isAdvisor = useMemo(() => ((me?.role ?? me?.user?.typeKey ?? '') as string).toUpperCase() === 'ADVISOR', [me?.role, me?.user?.typeKey]);

  useEffect(() => {
    let active = true;
    fetchCurrentUser()
      .then((resp) => { if (active) setMe(resp); })
      .catch(() => { if (active) setMe(null); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    fetchProductVariant(id)
      .then((response) => {
        if (isMounted) {
          setData(response);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load variant.');
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [id]);

  useEffect(() => {
    let active = true;
    setRelated(null);
    setRelatedError(null);
    if (!id) return;
    setRelatedLoading(true);
    fetchProductRelatedVariants(id)
      .then((items) => {
        if (!active) return;
        setRelated(items);
      })
      .catch((err) => {
        if (!active) return;
        const message = err instanceof Error ? err.message : 'Failed to load related variants.';
        setRelatedError(message);
      })
      .finally(() => {
        if (active) setRelatedLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  // Load inventory by variant ERP id
  useEffect(() => {
    if (!data?.erpId) return;
    let active = true;
    setInvLoading(true);
    setInvError(null);
    // fetch up to 100 locations for this variant
    fetchInventories({ page: 1, limit: 100, variantErpId: data.erpId })
      .then((resp) => { if (active) setInv(resp); })
      .catch((e) => { if (active) setInvError(e instanceof Error ? e.message : 'Failed to load inventory.'); })
      .finally(() => { if (active) setInvLoading(false); });
    return () => { active = false; };
  }, [data?.erpId]);

  const toggleActive = async () => {
    if (!data || savingStatus) return;
    setSavingStatus(true);
    try {
      const updated = await updateProductVariantActive(data.id, !data.isActive);
      setData(updated);
    } catch (e) {
      // Silently ignore for now; in a real UX we might surface a toast
    } finally {
      setSavingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="mt-6 flex-1 rounded-2xl bg-white px-6 py-5 text-sm text-slate-500 shadow-inner">
        Loading variant...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mt-6 flex-1 rounded-2xl bg-white px-6 py-5 text-sm text-danger shadow-inner">
        {error ?? (servicesOnly ? 'Service variant not found.' : 'Variant not found.')}
        <div className="mt-4">
          <button
            type="button"
            className="rounded-xl bg-primary px-4 py-2 text-sm text-white shadow"
            onClick={onBack}
          >
            Back to {servicesOnly ? 'service variants' : 'variants'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 rounded-2xl bg-white px-6 py-5 shadow-inner">
      {isAdvisor ? (
        <div className="flex flex-col gap-1">
          <h2 className="text-2xl font-semibold text-slate-800">{data.name}</h2>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">
            ERP ID {data.erpId} · {data.articleNumber ? `Article ${data.articleNumber}` : 'No article number'}
            {data.upc ? ` · UPC ${data.upc}` : ''}
          </p>
        </div>
      ) : (
        <PageHeaderWithBack
          title={data.name}
          description={<p className="mt-1 max-w-2xl text-sm text-slate-500">
            ERP ID {data.erpId} · {data.articleNumber ? `Article ${data.articleNumber}` : 'No article number'}
            {data.upc ? ` · UPC ${data.upc}` : ''}
          </p>}
          onBack={onBack}
          backLabel={`Back to ${servicesOnly ? 'service variants' : 'variants'}`}
          actions={servicesOnly && !isAdvisor ? (
            <button
              type="button"
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-primary disabled:opacity-60"
              onClick={toggleActive}
              disabled={savingStatus}
            >
              {savingStatus ? 'Updating…' : data.isActive ? 'Mark inactive' : 'Mark active'}
            </button>
          ) : undefined}
        />
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-[320px_1fr]">
        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
          <h3 className="text-base font-semibold text-slate-700">Metadata</h3>
          <dl className="mt-4 space-y-3 text-sm text-slate-700">
            <div className='mb-1'>
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">ERP ID</dt>
              <dd>{data.erpId}</dd>
            </div>

            <div className='mb-1'>
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Brand</dt>
              <dd>{data.brand?.name ?? '—'}</dd>
            </div>

            <div className='mb-1'>
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Source</dt>
              <dd>{data.isCreatedByWorkshopManagement ? 'Workshop management' : 'ERP'}</dd>
            </div>

            <div className='mb-1'>
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">UPC</dt>
              <dd>{data.upc ?? '—'}</dd>
            </div>

            <div className='mb-1'>
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Retail Price</dt>
              <dd>{data.retailPrice ? `${data.retailPrice} ${data.currencyCode ?? ''}` : '—'}</dd>
            </div>
            <div className='mb-1'>
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Master Product ERP</dt>
              <dd>{data.masterProductErpId ?? '—'}</dd>
            </div>
            <div className='mb-1'>
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Enabled</dt>
              <dd>{data.isActive ? 'Active' : 'Inactive'}</dd>
            </div>
            <div className='mb-1'>
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Last Updated</dt>
              <dd>{new Date(data.updatedAt).toLocaleString()}</dd>
            </div>
          </dl>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-base font-semibold text-slate-700">Related products &amp; services</h3>
            <button
              type="button"
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:border-primary hover:text-primary"
              onClick={() =>
                navigate(
                  servicesOnly
                    ? `/inventory/service-variants/${data.id}/related-products`
                    : `/inventory/variants/${data.id}/related-products`,
                )
              }
            >
              Manage related
            </button>
          </div>
          {relatedLoading ? (
            <p className="text-xs text-slate-400">Loading related variants…</p>
          ) : relatedError ? (
            <p className="text-xs text-danger">{relatedError}</p>
          ) : !related || related.length === 0 ? (
            <p className="text-xs text-slate-400">No related variants linked yet.</p>
          ) : (
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <table className="min-w-full text-left text-xs text-slate-600">
                <thead className="bg-slate-100 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2 font-semibold">Name</th>
                    <th className="px-4 py-2 font-semibold">Type</th>
                    <th className="px-4 py-2 font-semibold">ERP ID</th>
                    <th className="px-4 py-2 font-semibold">Brand</th>
                  </tr>
                </thead>
                <tbody>
                  {related.map((relation) => {
                    const v = relation.relatedVariant;
                    return (
                      <tr key={relation.id} className="even:bg-white">
                        <td className="px-4 py-2 font-medium text-slate-700">{v.name}</td>
                        <td className="px-4 py-2 text-slate-500">
                          {v.isCreatedByWorkshopManagement ? 'Service' : 'Product'}
                        </td>
                        <td className="px-4 py-2 text-slate-500">{v.erpId}</td>
                        <td className="px-4 py-2 text-slate-500">{v.brand?.name ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-base font-semibold text-slate-700">Attribute Values</h3>
            <span className="text-xs text-slate-400 ml-2">({data.values?.length ?? 0} total)</span>
          </div>
          {data.values?.length ? (
            <ul className="space-y-3">
              {data.values.map((value) => (
                <li key={value.id} className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                  <div className="text-sm font-semibold text-slate-700">
                    {value.attribute?.name ?? 'Attribute'}{' '}
                    <span className="text-xs text-slate-400">(ERP {value.attributeErpId})</span>
                  </div>
                  <div className="mt-1 text-xs text-slate-500">Value: {value.value}</div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-slate-400">No attribute values for this variant.</p>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-base font-semibold text-slate-700">Inventory by Location</h3>
            <span className="text-xs text-slate-400 ml-2">({inv?.total ?? 0} entries)</span>
          </div>
          <div className="overflow-hidden rounded-xl border border-slate-200">
            <table className="min-w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-semibold">Location</th>
                  <th className="px-4 py-2 font-semibold">Stock</th>
                </tr>
              </thead>
              <tbody>
                {invLoading ? (
                  <tr><td className="px-4 py-4 text-center text-slate-400" colSpan={2}>Loading inventory…</td></tr>
                ) : invError ? (
                  <tr><td className="px-4 py-4 text-center text-danger" colSpan={2}>{invError}</td></tr>
                ) : !inv || inv.data.length === 0 ? (
                  <tr><td className="px-4 py-4 text-center text-slate-400" colSpan={2}>No inventory for this variant.</td></tr>
                ) : (
                  inv.data.map((row) => (
                    <tr key={row.id} className="even:bg-white">
                      <td className="px-4 py-2">{row.location?.name ?? '—'}</td>
                      <td className="px-4 py-2">{row.stock}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VariantsDetailPanel;
